import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PaymentActionType } from "../AddPaymentMethods";
import { MakePaymentClick } from "components/sliderPage/makePayment/MakePayment";

import classes from "./CardForm.module.scss";
import { useRootDispatch, useRootSelector } from "store";
import {
  extractError,
  renderConditionalProp,
  getDefaultInputValue,
} from "utility";
import { getCardType } from "utility/cardType";
import { useRequestDispatch } from "store/requestHooks";
import { hasAccountPermission } from "utility/account";
import { selectedAccountInfo } from "store/account/account-selector";
import { AccountMatrix } from "store/account/account-state";
import { updateSlide } from "store/common/common-slice";
import { AxiosError } from "axios";
import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";
import {
  SavePaymentMethod,
  savePaymentMethod,
} from "store/paymentMethods/paymentMethods-action";
import {
  CardTokenResponses,
  useCardTokenization,
} from "hooks/useCardTokenization";

import { useInput, UseInput, useAddNotification } from "hooks";
import { Input, InputDTO } from "components/common";
import { EWalletConfiguration } from "store/config/ewallet-configuration";

interface CardFormProps {
  action?: PaymentActionType;
  buttonClick?: MakePaymentClick;
  updateButtonClick?: (
    updateFn: (prev: MakePaymentClick) => MakePaymentClick,
  ) => void;
  paymentHandler?: Function;
  effectiveConfig: EWalletConfiguration;
}

const CardForm: React.FC<CardFormProps> = ({
  action,
  buttonClick,
  updateButtonClick,
  paymentHandler,
  effectiveConfig,
}) => {
  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();
  const accountInfo = useRootSelector(selectedAccountInfo);
  const [disabled, setDisabled] = useState(false);
  const { tokenizeCard } = useCardTokenization(effectiveConfig);
  const addNotification = useAddNotification();

  useEffect(() => {
    if (
      !hasAccountPermission(
        accountInfo?.clientAttributes,
        AccountMatrix.EnableCard,
      )
    ) {
      setDisabled(true);
    }
  }, [accountInfo]);

  const { t } = useTranslation(["common", "payment_next"]);
  const [formState, setFormState] = useState<any>({});

  const cardNameInput: UseInput = useInput(getDefaultInputValue(""));
  const ack: UseInput = useInput(getDefaultInputValue(false));

  const cardExpression = "(\\d\\s?){13,19}";
  const expirationDateExpression = "(0[1-9]|1[0-2])/20\\d{2}";
  const cvv2Expression = "\\d{3,4}";
  const zipCodeExpression = "\\d{5}(-\\d{4})?";

  useEffect(() => {
    if (buttonClick?.scheduleFlag) {
      ack.setValue(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonClick?.scheduleFlag]);

  const getFieldValue = useCallback(
    (key: string) => {
      const refValue = formState[key]?.ref?.current?.value;
      return typeof refValue === "string" ? refValue.trim() : "";
    },
    [formState],
  );

  const applyDefaultValidation = useCallback(
    (el: HTMLInputElement | null, message: string) => {
      if (!el) return true;
      el.setCustomValidity("");
      if (!el.validity.valid) {
        el.setCustomValidity(message);
      }
      return el.checkValidity();
    },
    [],
  );

  const validateCardNumberEl = useCallback(
    (el: HTMLInputElement | null) => {
      if (!el) return true;
      el.setCustomValidity("");
      if (!el.validity.valid) {
        el.setCustomValidity(
          t("common:validation_length", { min: 13, max: 16 }),
        );
      } else if (!luhnCheck(getFieldValue("cardNumber"))) {
        el.setCustomValidity(t("common:invalid-card-information"));
      }
      return el.checkValidity();
    },
    [getFieldValue, t],
  );

  const validateExpirationEl = useCallback(
    (el: HTMLInputElement | null) => {
      if (!el) return true;
      el.setCustomValidity("");
      if (!el.validity.valid) {
        el.setCustomValidity(t("common:expiry_validation"));
      } else if (!isValidExpiration(getFieldValue("expirationDate"))) {
        el.setCustomValidity(t("common:expiry_validation_past"));
      }
      return el.checkValidity();
    },
    [getFieldValue, t],
  );

  const validateFields = useCallback(() => {
    let formValid = false;
    const isNameValid = applyDefaultValidation(
      formState.nameOnCard.ref.current,
      t("common:field_not_empty"),
    );
    const isCardValid = validateCardNumberEl(formState.cardNumber.ref.current);
    const isExpireValid = validateExpirationEl(
      formState.expirationDate.ref.current,
    );
    const isCvv2Valid = applyDefaultValidation(
      formState.cvv2.ref.current,
      t("common:validation_length", { min: 3, max: 4 }),
    );
    const isZipValid = applyDefaultValidation(
      formState.zipCode.ref.current,
      t("common:zipcode_validation"),
    );
    const isAddressValid = applyDefaultValidation(
      formState.addressLine1.ref.current,
      t("common:field_not_empty"),
    );
    if (
      isNameValid &&
      isCardValid &&
      isExpireValid &&
      isCvv2Valid &&
      isZipValid &&
      isAddressValid
    ) {
      formValid = true;
    }

    if (
      !cardNameInput.value &&
      (action === PaymentActionType.SAVE ||
        (action === PaymentActionType.SAVE_AND_PAY_LATER && ack.value))
    ) {
      const isNickNameValid = cardNameInput.ref.current?.reportValidity();
      formValid = formValid && isNickNameValid;
    }

    return formValid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formState,
    ack,
    cardNameInput,
    action,
    getFieldValue,
    t,
    validateCardNumberEl,
    validateExpirationEl,
    applyDefaultValidation,
  ]);

  const formatExpiration = (value: string) => {
    const expiration = value.replace(/[^\d]/g, "");
    if (expiration.length === 0) {
      return "";
    }

    if (expiration.length <= 2) {
      return expiration;
    }

    const month = expiration.slice(0, 2);
    let year = expiration.slice(2);

    if (year.length === 2) {
      year = `20${year}`;
    }
    return `${month}/${year}`;
  };

  const formatCardNumber = (value: string) => {
    value = value?.replace(/[^\d]/g, "");

    if (value.length > 4) {
      value = value.match(/.{1,4}/g)?.join(" ") ?? value;
    }

    if (value.length > 19) {
      value = value.slice(0, 19);
    }
    return value;
  };

  const saveMethod = useCallback(
    async (method: PaymentMethod) => {
      requestDispatch<SavePaymentMethod, PaymentMethod>(
        savePaymentMethod,
        method,
      )
        .unwrap()
        .then((res: PaymentMethod) => {
          if (buttonClick?.count) {
            if (paymentHandler) paymentHandler({ ...res, ack: true });
          } else {
            dispatch(updateSlide({ id: "Confirmation" }));
          }
        })
        .catch((err: AxiosError) => {
          const errCode = extractError(err);
          addNotification({
            messages: errCode,
            type: "ERROR",
            closable: true,
          });
        });
    },
    [
      buttonClick?.count,
      dispatch,
      paymentHandler,
      requestDispatch,
      addNotification,
    ],
  );

  const resetButtonClick = useCallback(() => {
    if (updateButtonClick && buttonClick?.count) {
      updateButtonClick((prev) => ({
        ...prev,
        count: 0,
      }));
    }
  }, [buttonClick?.count, updateButtonClick]);

  const accountType = accountInfo?.accountType;

  const saveOrPayHandler = useCallback(async () => {
    const isValid = validateFields();

    if (isValid) {
      const cardNumberValue = formatCardNumber(getFieldValue("cardNumber"));
      const expirationValue = formatExpiration(getFieldValue("expirationDate"));
      const cvv2Value = getFieldValue("cvv2");

      // set the formData then tokenize it
      const formData: PaymentMethod = {
        cardHolderName: getFieldValue("nameOnCard"),
        paymentMethodName: cardNameInput.value,
        ack: ack.value,
        expirationDate: expirationValue.replace(/\//g, ""),
        cvv2: cvv2Value,
        paymentMethodType: 2,
        address: {
          line1: getFieldValue("addressLine1"),
          line2: getFieldValue("addressLine2"),
          zip: getFieldValue("zipCode"),
        },
        cardType: getCardType(cardNumberValue),
        tokenizedCardData: cardNumberValue,
        lastFour: cardNumberValue.slice(-4),
      };

      const tokenResult = await tokenizeCard(formData);
      if (
        tokenResult?.response === CardTokenResponses.SUCCESS &&
        tokenResult.data
      ) {
        if (tokenResult.data.ack) {
          // This means we need to save the payment method
          try {
            tokenResult.data.accountType = accountType;
            await saveMethod(tokenResult.data);
          } catch (e) {
            console.log(e);
          }
        } else {
          // We do not need to save the payment method so we proceed with payment flow
          if (paymentHandler != null) await paymentHandler(tokenResult.data);
        }
      }
    } else {
      resetButtonClick();
    }
  }, [
    getFieldValue,
    paymentHandler,
    saveMethod,
    tokenizeCard,
    validateFields,
    ack.value,
    cardNameInput.value,
    resetButtonClick,
    accountType,
  ]);

  useEffect(() => {
    if (buttonClick?.count) {
      saveOrPayHandler();
    }
    return () => {
      resetButtonClick();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonClick?.count, resetButtonClick]);

  const luhnCheck = (cardNumber: string | null | undefined): boolean => {
    if (!cardNumber) {
      return false;
    }

    const cleanNumber = cardNumber.replace(/\D/g, "");
    let sum = 0;
    let shouldDouble = false;

    //go through the digits from end to start
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber.charAt(i), 10);

      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  };

  const isValidExpiration = (val: string) => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    const [monthStr, yearStr] = val.split("/");
    const expirationMonth = parseInt(monthStr, 10);
    const expirationYear = parseInt(yearStr, 10);

    if (
      expirationYear > currentYear ||
      (expirationYear === currentYear && expirationMonth >= currentMonth)
    )
      return true;

    return false;
  };

  const setFormData = useCallback((key: string, val: any) => {
    setFormState((state: any) => {
      return {
        ...state,
        [key]: { ...val, value: val.value },
      };
    });
  }, []);

  const handleCardInput = (e: any, input: UseInput) => {
    const rawValue = e.target.value;
    const formattedValue = formatCardNumber(rawValue);
    input.valueChangeHandler({
      ...e,
      target: { ...e.target, value: formattedValue },
    } as Event);
  };

  const handleExpirationChange = (e: any, input: UseInput) => {
    const rawValue = (e.target as HTMLInputElement).value;

    const isBackspace = e.nativeEvent.inputType.startsWith("delete");
    if (isBackspace) {
      input.valueChangeHandler(e);
      return;
    }

    const formattedValue = formatExpiration(rawValue);
    input.valueChangeHandler({
      ...e,
      target: { ...e.target, value: formattedValue },
    } as Event);
  };

  const handleCvv2Change = (e: any, input: UseInput) => {
    const rawValue = (e.target as HTMLInputElement).value;
    const formattedValue = rawValue.replace(/\D/g, "").slice(0, 4);
    input.valueChangeHandler({
      ...e,
      target: { ...e.target, value: formattedValue },
    } as Event);
  };

  const slimCdFieldTestIds: Record<string, string> = {
    nameOnCard: "add-slimcd-card-payment-method-card-holder-name",
    cardNumber: "add-slimcd-card-payment-method-card-number",
    expirationDate: "add-slimcd-card-payment-method-expiry",
    cvv2: "add-slimcd-card-payment-method-cvv",
    addressLine1: "add-slimcd-card-payment-method-address-line-1",
    addressLine2: "add-slimcd-card-payment-method-address-line-2",
    zipCode: "add-slimcd-card-payment-method-zip-code",
  };

  const renderFields = (fields: InputDTO[]) => {
    return fields.map((field: InputDTO, i) => {
      if (disabled) field.extraProp = { ...field.extraProp, disabled };
      return (
        <div key={i} className={classes.Form_Field}>
          <Input
            id={field.key}
            passState={setFormData}
            label={field.label}
            val={field.val}
            validationMessage={field.validationMessage}
            extraProp={field.extraProp}
            type={field.type}
            options={field.options}
            datacy={slimCdFieldTestIds[field.key]}
            onChange={field.onChange}
            native={field.native}
            onValidate={field.onValidate}
          />
        </div>
      );
    });
  };

  const formData: InputDTO[] = [
    {
      label: t("payment_next:name_label"),
      key: "nameOnCard",
      val: "",
      validationMessage: t("common:field_not_empty"),
      extraProp: {
        required: true,
        maxLength: 100,
        autoComplete: "cc-name",
      },
      native: true,
    },
    {
      label: effectiveConfig.debitCardOnly
        ? t("payment_next:debit_number_label")
        : t("payment_next:card_number_label"),
      key: "cardNumber",
      val: "",
      validationMessage: t("common:validation_length", {
        min: 13,
        max: 16,
      }),
      extraProp: {
        required: true,
        minLength: 16,
        maxLength: 19,
        pattern: cardExpression,
        autoComplete: "cc-number",
      },
      onChange: handleCardInput,
      native: true,
      onValidate: validateCardNumberEl,
    },
    {
      label: t("payment_next:expiry_label"),
      key: "expirationDate",
      val: "",
      validationMessage: t("common:expiry_validation"),
      extraProp: {
        required: true,
        maxLength: 7,
        pattern: expirationDateExpression,
        autoComplete: "cc-exp",
      },
      onChange: handleExpirationChange,
      native: true,
      onValidate: validateExpirationEl,
    },
    {
      label: t("payment_next:cvv_label", { defaultValue: "CVV" }),
      key: "cvv2",
      val: "",
      validationMessage: t("common:validation_length", {
        min: 3,
        max: 4,
      }),
      extraProp: {
        required: true,
        minLength: 3,
        maxLength: 4,
        pattern: cvv2Expression,
        autoComplete: "cc-csc",
      },
      onChange: handleCvv2Change,
      native: true,
    },
    {
      label: t("payment_next:card_address-line1"),
      key: "addressLine1",
      val: "",
      validationMessage: t("common:field_not_empty"),
      extraProp: {
        required: true,
        maxLength: 100,
        autoComplete: "address-line1",
      },
      native: true,
    },
    {
      label: t("payment_next:card_address-line2"),
      key: "addressLine2",
      val: "",
      validationMessage: " ",
      extraProp: {
        maxLength: 60,
        autoComplete: "address-line2",
      },
      native: true,
    },
    {
      label: t("payment_next:zip_code"),
      key: "zipCode",
      val: "",
      validationMessage: t("common:zipcode_validation"),
      extraProp: {
        required: true,
        minLength: 5,
        maxLength: 10,
        pattern: zipCodeExpression,
        autoComplete: "postal-code",
      },
      native: true,
    },
  ];

  return (
    <div className={classes.ModuleWrapper}>
      <form id="cardForm">
        <fieldset disabled={disabled}>
          {renderFields(formData)}
          {action === PaymentActionType.SAVE ||
          (action === PaymentActionType.SAVE_AND_PAY_LATER && ack.value) ? (
            <ft-textfield
              id="cardNickname"
              name="cardNickname"
              ref={cardNameInput.ref}
              data-testid="add-slimcd-card-payment-method-nickname"
              label={t("payment_next:card_nickname")}
              placeholder={t("payment_next:card_nickname")}
              maxLength={50}
              required
              outlined
              validationMessage={t("common:field_not_empty")}
              onInput={cardNameInput.valueChangeHandler}
              value={formState.cardNickname}
              disabled={disabled ? disabled : null}
            ></ft-textfield>
          ) : (
            <></>
          )}
          {action === PaymentActionType.PAY ? null : (
            <>
              <ft-formfield
                class={classes.Tnc}
                label={
                  action === PaymentActionType.SAVE
                    ? t("payment_next:card_review")
                    : t("payment_next:save_method")
                }
              >
                <ft-checkbox
                  id="ack"
                  name="ack"
                  ref={ack.ref}
                  onClick={
                    buttonClick?.scheduleFlag ? null : ack.valueCheckedHandler
                  }
                  {...renderConditionalProp(ack.value, "checked")}
                  {...renderConditionalProp(
                    buttonClick?.scheduleFlag ? true : false,
                    "disabled",
                  )}
                  required={true}
                  validationMessage={"error"}
                  data-testid="add-payment-method-type-review-and-tnc"
                ></ft-checkbox>
              </ft-formfield>
              <p className={classes.Confirm}>
                {action === PaymentActionType.SAVE && ack.value !== true
                  ? t("payment_next:ach_save_confirm")
                  : ""}
              </p>
            </>
          )}
        </fieldset>
        <div className={classes.FormControl}>
          {action === PaymentActionType.SAVE ? (
            <div className={classes.FormButtons}>
              <ft-button
                class={[classes.Back, "outline-button"].join(" ")}
                label={t("common:back_label")}
                outlined
                onClick={() => {
                  dispatch(updateSlide({ id: "ManageWallet" }));
                }}
                data-testid="add-payment-method-back"
              ></ft-button>
              <ft-button
                class={classes.Create}
                label={t("common:save_label")}
                unelevated
                onClick={saveOrPayHandler}
                data-testid="add-slimcd-card-payment-method-save"
                {...renderConditionalProp(ack.value !== true, "disabled")}
              ></ft-button>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default CardForm;
